import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import _ from "lodash";
import moment from "moment";
import { compose, graphql } from "lib/apolloClient";
import withDerivedProps from "lib/withDerivedProps";
import client from "lib/apolloClient/client";

import {
  Modal,
  Button,
  ToggleSwitch,
  Tooltip,
  IconButton,
} from "@toddle-edu/ds-web";
import {
  AddCircleOutlined,
  BellCancelledOutlined,
  BellRingOutlined,
} from "@toddle-edu/ds-icons";
import UIModal from "UIComponents/UIModal";
import withLoader from "UIComponents/GqlWithLoader";
import FullScreenLoader from "UIComponents/FullScreenLoader";

// Graphql and modules
import { getAssessmentQuery } from "Assessments/modules/AssessmentQuery";
import { getStaffCourseGenericFoldersBasicDetailsQuery } from "Teacher/modules/TeacherQuery";
import { getSettingValue } from "Utils/ConfigurationUtils";
import { WithConfigurationsAndPreferences } from "Utils/ConfigurationUtils/hoc";
import { generateConfigurationQueryVariablesV2 } from "Utils/ConfigurationUtils/Utils";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getSourceAssignmentDetailsQuery } from "ClassRoom/modules/ClassRoomQuery";

// Utils
import {
  getSettingGroups,
  getAssessmentData,
  getGenericFolders,
  getAssignmentCommonSettings,
  getWorksheetSettings,
  getAssignmentClassWiseSettings,
  checkIfSelectedDatesNotInRange,
  getPrefilledValuesFromDefaultCourse,
  getMomentFromDateAndTimeString,
  shouldPrefillOpenDateFromCourse,
  shouldPrefillDueDateFromCourse,
  shouldPrefillCloseDateFromCourse,
  getUpdatedCoursesWithDefaultDueDate,
  isTurnitinEnabledForGrade,
} from "./Utils";
import { getCalendarEventsViewSettingValue } from "MyCalendar/modules/Utils";
import { checkIsSubmissionPendingForAll } from "ClassRoom/modules/utils";
import ACLStore from "lib/aclStore";
import {
  getFormattedCloseDateTimeString,
  getAdvancedTimelineSettingsOptions,
  getShowUpdateAITutorModal,
  getBackDateAssignmentCoursesV2,
} from "../Utils";
import {
  getSelectedCurriculumId,
  getSelectedGrades,
  getSelectedAcademicYearId,
} from "modules/CommonSelectors";
import { checkIsDateSame, checkIsDateBefore } from "Utils/DateTimeHelpers";
import { useI18n } from "Hooks";
import { getDateTimeStringsBasedOnFormat } from "Utils";
import { generateRnPContextsByLevel } from "Utils/RnPHelpers";
import {
  OPENS_ON,
  DUE_ON,
  CLOSES_ON,
  VISIBILITY,
  VISIBLE,
  CONFIGURE_ASSIGN_SETTINGS_SEEN,
} from "Constants/stringConstants";

import AssignClassSettingsDataGridWrapper from "./AssignClassSettingsDataGridWrapper";
import AssignmentCommonSettings from "./AssignmentCommonSettings";
import AdvancedTimelineSettings from "AppComponents/AdvancedTimelineSettings";
import AddClassModal from "../AddClassModal";
import WithOnlySettingStep from "../WithOnlySettingStep";

import classes from "./AssignmentSettings.scss";
import DeliverySettingsV2 from "./DeliverySettingsV2";
import {
  CURRICULUM_LEVEL,
  UPDATE_USER_CURRICULUM_SETTINGS,
} from "Constants/permissionConstants";
import { updateSettings } from "modules/ConfigurationThunks";
import { getIsLearningCoursesEnabled } from "LearningCourses/modules/Utils";
import withControlParameters from "lib/aclService/hoc/withControlParameters";
import { setLtiAttachmentDeleted } from "UnitPlans/components/Assessments/modules/AssessmentModule/AssessmentActions";
const styles = {
  tooltipOverlayInnerStyle: {
    maxWidth: "176px",
    textAlign: "center",
  },
};

const checkHasTurnitinAttachment = (studentTemplates = []) => {
  return _.some(studentTemplates, template => {
    const attachmentGroups = _.get(template, "attachmentGroups", []);
    return _.some(attachmentGroups, group => {
      const attachments = _.get(group, "attachments", []);
      return _.some(attachments, attachment => {
        const service = _.get(attachment, "metadata.service");
        return service === "TURNITIN";
      });
    });
  });
};

const checkIfAnyCourseHasCloseSubmissionDate = ({ courses }) => {
  return _.some(
    courses,
    course => !_.isEmpty(_.get(course, "closeSubmissionDate", null))
  );
};

const checkIfAnyCourseHasDueDate = ({ courses }) => {
  return _.some(
    courses,
    course => !_.isEmpty(_.get(course, "deadlineDate", null))
  );
};

const checkIfAnyCourseHasDiffOpenAndCreationDate = ({ courses, createdAt }) => {
  return _.some(courses, course => {
    const { scheduleDate, scheduleTime } = _.get(course, "state", {});
    if (_.isEmpty(createdAt)) {
      return !_.isEmpty(scheduleDate);
    }
    return !checkIsDateSame({
      date1: moment(moment(createdAt).utc(true).toISOString()),
      date2: moment(`${scheduleDate}:${scheduleTime}`, "YYYY-MM-DD:HH:mm"),
      check: "minute",
    });
  });
};

const checkIsAnyCoursHasSetVisibility = ({ courses }) => {
  return _.some(
    courses,
    ({ visibility } = {}) => !_.isEmpty(visibility) && visibility !== VISIBLE
  );
};

const checkIfStaffHasAccessToAllClassesSelected = ({
  courses,
  filteredStaffCoursesIds,
}) => {
  return _.every(courses, course =>
    _.includes(filteredStaffCoursesIds, course?.id)
  );
};

const getInitialTimelineSettings = ({
  sourceFrom,
  courses,
  createdAt,
  userTimelineSettingsPreference,
  showCalendarAssignmentByScheduleDate,
  metadata,
  isFixedStartTime,
  t,
  defaultCourseFromSourceAssignment,
  studentTemplates = [],
}) => {
  const options = getAdvancedTimelineSettingsOptions(t);

  const assignmentLevelPreferences = _.get(metadata, "preferences", []);

  const hasTurnitin = checkHasTurnitinAttachment(studentTemplates);

  return _.reduce(
    options,
    (initialSettings, { key }) => {
      if (
        (_.includes(userTimelineSettingsPreference, key) ||
          _.includes(assignmentLevelPreferences, key)) &&
        !isFixedStartTime
      ) {
        initialSettings.push(key);
      } else {
        switch (key) {
          case OPENS_ON: {
            if (
              hasTurnitin ||
              (sourceFrom === "CALENDAR" &&
                showCalendarAssignmentByScheduleDate) ||
              checkIfAnyCourseHasDiffOpenAndCreationDate({
                courses,
                createdAt,
              }) ||
              isFixedStartTime ||
              shouldPrefillOpenDateFromCourse(defaultCourseFromSourceAssignment)
            ) {
              initialSettings.push(key);
            }
            break;
          }
          case DUE_ON:
            if (
              checkIfAnyCourseHasDueDate({ courses }) ||
              sourceFrom === "CALENDAR" ||
              isFixedStartTime ||
              shouldPrefillDueDateFromCourse(defaultCourseFromSourceAssignment)
            ) {
              initialSettings.push(key);
            }
            break;
          case CLOSES_ON:
            if (
              checkIfAnyCourseHasCloseSubmissionDate({ courses }) ||
              isFixedStartTime ||
              shouldPrefillCloseDateFromCourse(
                defaultCourseFromSourceAssignment
              )
            ) {
              initialSettings.push(key);
            }
            break;
          case VISIBILITY:
            if (
              isFixedStartTime ||
              checkIsAnyCoursHasSetVisibility({ courses })
            ) {
              initialSettings.push(key);
            }
            break;
        }
      }
      return initialSettings;
    },
    []
  );
};

const AssignmentSettings = props => {
  const {
    curriculumProgram,
    isClassDiscussionEnabled,
    updateSelectedItems,
    courses,
    leId,
    academicYearId,
    courseId,
    isStudentSubmissionEnabled,
    contentType,
    genericFolders,
    portalType,
    studentTemplates,
    selectedTimeSlot,
    mode,
    assignmentId,
    isSimilarityCheckEnabled,
    isTurnitinEnabledForGrade,
    isQt,
    scoreCategoryLabel,
    subjectName,
    courseIdsOfScoreCategorySet,
    assignmentTags,
    grades,
    subjects,
    showCalendarAssignmentByScheduleDate,
    subjectsField,
    categoryDetails,
    isExcludedFromFinalGradeCalculationEnabled,
    isExcludedFromFinalGradeCalculation,
    toggleSettingsModal,
    onClickDone,
    title,
    isWorksheet,
    state,
    isTimedTestEnabled,
    isFixedStartTime,
    isLockScreenEnabled,
    isSafeBrowserEnabled,
    timedTestDuration,
    isDraftMode,
    isDpEp,
    isDpPa,
    assignmentData = {},
    isOnScreenCalculatorEnabled,
    showStudentResponse,
    isDuplicateAssignment = false,
    isFromClassMaterials,
    isSharedWithClassMaterial,
    setBackDateModalParams,
    assessmentType,
    assignedStudents,
    userTimelineSettingsPreference,
    createdAt,
    sourceFrom,
    setToastMsg,
    defaultCourseFromSourceAssignment = {},
    metadata,
    isAITutor,
    isAITutorUpdated,
    setCurrentDialogWithAlert,
    dueTimePreference,
    sourceAssignmentCommonSettings,
    assignmentLevelPreferences,
    sourceAssignmentWorksheetSettings,
    updateSettings,
    userId,
    filteredStaffCoursesIds,
    learningCourseId,
    learningCourseClassIds,
    isIncludedInCourseFlow,
    isCourseFlow,
    isLearningCoursesEnabled,
    updateSelectedClassItems,
    assignmentDefaultSettings,
    isSendAssignedNotification,
    state: assignmentState,
    primaryGradeId,
    isLtiAttachmentDeleted,
    setLtiAttachmentDeleted,
    taskTypeGroup,
    hasUnitPlan,
  } = props;

  const { t } = useI18n();

  const [showAddClassModal, setShowAddClassModal] = useState(false);
  const [coursesSearchText, setCoursesSearchText] = useState("");
  const [selectedGradeIds, setSelectedGradeIds] = useState([]);
  const [currentTimelineSettings, setCurrentTimelineSettings] = useState(() =>
    getInitialTimelineSettings({
      sourceFrom,
      courses,
      createdAt,
      userTimelineSettingsPreference,
      showCalendarAssignmentByScheduleDate,
      metadata,
      isFixedStartTime,
      t,
      defaultCourseFromSourceAssignment,
      studentTemplates,
    })
  );

  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);

  const doneButtonLabel =
    mode === "create" || state === "DRAFT"
      ? t("common:assign")
      : t("common:save_changes");

  const isSubmissionPendingForAll = checkIsSubmissionPendingForAll({
    assignedStudents,
  });

  const classWiseSettingsToDisplay = getAssignmentClassWiseSettings({
    t,
    currentTimelineSettings,
    isFixedStartTime,
    isTimedTestEnabled,
  });

  // In new course setup, while editing assignment -> if staff does not have access to all linked assignments classes...
  // then disable all common settings toggle like submission on/off, class discussion on/off, etc.
  const doesStaffHaveAccessToAllClasses = useMemo(
    () =>
      !isLearningCoursesEnabled ||
      mode === "create" ||
      (mode === "edit" &&
        checkIfStaffHasAccessToAllClassesSelected({
          courses,
          filteredStaffCoursesIds,
        })),

    [courses, filteredStaffCoursesIds]
  );

  const showAddClassBtn = useMemo(() => {
    if (!isLearningCoursesEnabled) return true;

    const addedClassIds = _.map(courses, "id");

    return _.some(learningCourseClassIds, id => !_.includes(addedClassIds, id));
  }, [courses, isLearningCoursesEnabled, learningCourseClassIds]);

  // TODO: Fix logic
  useEffect(() => {
    if (!isLtiAttachmentDeleted) {
      return;
    }

    const updatedSettings = _.without(currentTimelineSettings, OPENS_ON);

    updateSelectedItems({
      courses,
      metadata: { preferences: updatedSettings },
    });

    setCurrentTimelineSettings(updatedSettings);

    setLtiAttachmentDeleted(false);
  }, [isLtiAttachmentDeleted]);

  useEffect(() => {
    setCurrentTimelineSettings(() =>
      getInitialTimelineSettings({
        sourceFrom,
        courses,
        createdAt,
        userTimelineSettingsPreference,
        showCalendarAssignmentByScheduleDate,
        metadata,
        isFixedStartTime,
        t,
        defaultCourseFromSourceAssignment,
        studentTemplates,
      })
    );
    updateSelectedItems({
      metadata: {
        preferences: getInitialTimelineSettings({
          sourceFrom,
          courses,
          createdAt,
          userTimelineSettingsPreference,
          showCalendarAssignmentByScheduleDate,
          metadata,
          isFixedStartTime,
          t,
          defaultCourseFromSourceAssignment,
          studentTemplates,
        }),
      },
    });
  }, [isFixedStartTime]);

  useEffect(() => {
    // clear close date if submission disabled
    if (!isStudentSubmissionEnabled) {
      const updatedCourses = _.map(courses, course => ({
        ...course,
        closeSubmissionDate: null,
      }));
      updateSelectedItems({ courses: updatedCourses });
      removeCloseDateFromTimelineSettings();
    }
  }, [isStudentSubmissionEnabled]);

  const removeCloseDateFromTimelineSettings = () => {
    const updatedSettings = _.filter(
      currentTimelineSettings,
      key => key !== CLOSES_ON
    );
    setCurrentTimelineSettings(updatedSettings);
    updateSelectedItems({
      metadata: { preferences: updatedSettings },
    });
  };

  const coursesIdentifier = useMemo(() => {
    return _.map(courses, course => ({
      id: course.id,
      deadlineDate: course.deadlineDate,
      deadlineTime: course.deadlineTime,
    }));
  }, [courses]);

  const ltiAttachmentSignature = useMemo(() => {
    return JSON.stringify(
      _.map(studentTemplates, template => {
        const attachmentGroups = _.get(template, "attachmentGroups", []);
        return _.compact(
          _.map(attachmentGroups, group => {
            const attachment = _.first(_.get(group, "attachments", []));
            const attachmentType = _.get(attachment, "metadata.type");
            const attachmentService = _.get(attachment, "metadata.service");

            if (
              attachmentType === "LTI_TOOL" ||
              attachmentService === "TURNITIN"
            ) {
              return {
                id: attachment.id,
                type: attachmentType,
                service: attachmentService,
                dueDate: _.get(attachment, "metadata.custom.default_duedate"),
                startDate: _.get(
                  attachment,
                  "metadata.custom.default_startdate"
                ),
              };
            }
            return null;
          })
        );
      })
    );
  }, [studentTemplates]);

  // TODO: Fix logic
  useEffect(() => {
    if (isLtiAttachmentDeleted) {
      return;
    }

    const result = getUpdatedCoursesWithDefaultDueDate({
      courses,
      leId,
      academicYearId,
      forceUpdate: _.every(
        courses,
        course =>
          _.isEmpty(course.deadlineDate) &&
          _.isEmpty(course.state?.scheduleDate)
      ),
    });

    if (!result) {
      return;
    }

    const {
      updatedCourses,
      shouldAddDueOnSetting,
      shouldAddOpensOnSetting,
    } = result;

    if (!_.isEqual(courses, updatedCourses)) {
      updateSelectedItems({ courses: updatedCourses });

      const newSettings = [...currentTimelineSettings];

      if (
        shouldAddOpensOnSetting &&
        !_.includes(currentTimelineSettings, OPENS_ON)
      ) {
        newSettings.push(OPENS_ON);
      }

      if (
        shouldAddDueOnSetting &&
        !_.includes(currentTimelineSettings, DUE_ON)
      ) {
        newSettings.push(DUE_ON);
      }

      if (!_.isEqual(newSettings, currentTimelineSettings)) {
        handleTimelineSettingsChange(newSettings);
      }
    }
  }, [
    ltiAttachmentSignature,
    leId,
    academicYearId,
    currentTimelineSettings,
    mode,
    isLtiAttachmentDeleted,
  ]);

  const setCloseDateSameAsDueDate = () => {
    const updatedCourses = _.map(courses, course => {
      const { deadlineDate, deadlineTime } = course;
      if (_.isEmpty(deadlineDate)) {
        return course;
      }
      const deadline = getMomentFromDateAndTimeString({
        dateString: deadlineDate,
        timeString: deadlineTime,
      });
      const isDeadlineGonePast = checkIsDateBefore({
        date1: deadline,
        date2: moment(),
        key: "minute",
      });
      if (!isDeadlineGonePast) {
        return {
          ...course,
          closeSubmissionDate: `${deadlineDate}:${deadlineTime}`,
        };
      }
      return course;
    });
    updateSelectedItems({ courses: updatedCourses });
  };

  const handleTimelineSettingsChange = params => {
    const updatedSettings = _.filter(
      params,
      key => key !== CONFIGURE_ASSIGN_SETTINGS_SEEN
    );
    setCurrentTimelineSettings(updatedSettings);
    updateSelectedItems({
      metadata: { preferences: updatedSettings },
    });

    // If user configures close date after due date and if due date is already added then copy due date into close date
    if (
      !_.includes(currentTimelineSettings, CLOSES_ON) &&
      _.includes(updatedSettings, CLOSES_ON) &&
      isStudentSubmissionEnabled
    ) {
      setCloseDateSameAsDueDate();
    }
  };

  const getModalHeaderTitle = () => {
    switch (mode) {
      case "create":
        return t("classRoom:assign_with_label_in_quotes", { label: title });
      default:
        return t("classRoom:edit_settings_for_with_label", { label: title });
    }
  };

  const handleAddClassClick = () => {
    setShowAddClassModal(true);
  };

  const handleAddClassModalClose = () => {
    setShowAddClassModal(false);
    setCoursesSearchText("");
    setSelectedGradeIds([]);
  };

  const handleAddClasses = newCourses => {
    const defaultCourse =
      _.first(courses) || defaultCourseFromSourceAssignment || {};

    updateSelectedItems({
      courses: [
        ...courses,
        ...getPrefilledValuesFromDefaultCourse({ newCourses, defaultCourse }),
      ],
    });
    handleAddClassModalClose();
  };

  const handleClose = () => {
    if (
      checkIfSelectedDatesNotInRange({
        courses,
        isDraftMode,
        setToastMsg,
        currentTimelineSettings,
        assignmentData,
      })
    ) {
      return;
    }

    if (state !== "DRAFT" && !isLearningCoursesEnabled) {
      // reset selected courses on cancel click
      updateSelectedItems({
        courses: _.filter(courses, { id: courseId }),
      });
    }
    toggleSettingsModal();
  };

  const handleAssignOrScheduleClick = async () => {
    if (
      checkIfSelectedDatesNotInRange({
        courses,
        isDraftMode,
        setToastMsg,
        currentTimelineSettings,
        assignmentData,
      })
    ) {
      return;
    }

    const oldCourses = _.get(assignmentData, "courses", []);
    const isBackdateAssignment = _.get(
      assignmentData,
      "isBackdateAssignment",
      false
    );

    const params =
      mode === "edit" && state === "DRAFT"
        ? { assessmentState: "PUBLISHED", genericFolders }
        : { genericFolders };

    const {
      backDateCourses,
      hasBothOpenAndDueDateBackdated = false,
    } = getBackDateAssignmentCoursesV2({
      courses,
      oldCourses,
      assignmentLevelPreferences,
      isBackdateAssignment,
    });
    if (!_.isEmpty(backDateCourses)) {
      setBackDateModalParams({
        onClickDoneParams: params,
        buttonLabelText: doneButtonLabel,
        hasBothOpenAndDueDateBackdated,
        backDateCourses,
      });
      return;
    }

    // when tutor is update and few students have already started the conversation
    // then show a modal to let the teacher know that "Students will be notified that changes have been made and will be allowed to reset their session with the tutor."
    if (isAITutorUpdated && mode === "edit") {
      setShowFullScreenLoader(true);
      const showUpdateAITutorModal = await getShowUpdateAITutorModal({
        assignmentId,
      });

      setShowFullScreenLoader(false);

      if (showUpdateAITutorModal) {
        setCurrentDialogWithAlert("UPDATE_AI_TUTOR");
        return;
      }
    }

    if (isWorksheet) {
      const currentCurriculumProgramId = _.get(curriculumProgram, "id");
      const responseSettingValue = getSettingValue({
        name: "ResponseVisibilityConfiguration",
        userId,
        currentCurriculumProgramId,
      });

      /**
       * Refetches the worksheet monitoring data from the server.
       * This function ensures that the latest monitoring data is available,
       * after changes in the assignment settings.
       */
      const refetchMonitoringData = () => {
        client.refetchQueries({
          include: ["getWorksheetMonitoringData"],
        });
      };

      if (!_.isEqual(responseSettingValue, showStudentResponse)) {
        await updateSettings({
          settings: [
            {
              name: "ResponseVisibilityConfiguration",
              value: showStudentResponse,
            },
          ],
          currentCurriculumProgramId,
          queryVariablesSrc: CURRICULUM_LEVEL,
          mutationVariablesSrc: UPDATE_USER_CURRICULUM_SETTINGS,
          showToastMsg: false,
          portalType,
          onSettingsUpdate: refetchMonitoringData,
        });
      }

      await onClickDone(params);
      refetchMonitoringData();
      return;
    }

    onClickDone(params);
  };

  const handleDraftOrCancelClick = () => {
    if (
      checkIfSelectedDatesNotInRange({
        courses,
        isDraftMode,
        setToastMsg,
        currentTimelineSettings,
        assignmentData,
      })
    ) {
      return;
    }

    if (!isDraftMode) {
      handleClose();
      return;
    }

    const params = {
      assessmentState: "DRAFT",
      shouldSelectAtleastOneStudent: false,
      genericFolders,
    };

    onClickDone(params);
  };

  const handleSubmissionToggleChange = () => {
    const params = {
      isStudentSubmissionEnabled: !isStudentSubmissionEnabled,
    };
    if (!isStudentSubmissionEnabled) {
      params.isSimilarityCheckEnabled = false;
    }
    updateSelectedItems(params);
  };

  const handleToggleSendAssignNotification = () => {
    updateSelectedItems({
      isSendAssignedNotification: !isSendAssignedNotification,
    });
    try {
      updateSettings({
        settings: [
          {
            name: "SendAssignNotificationPreference",
            value: JSON.stringify(!isSendAssignedNotification),
          },
        ],
        showToastMsg: false,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const renderAddClassModal = () => {
    return (
      <UIModal
        isOpen={true}
        modalContent={classes.addClassModalContainer}
        onRequestClose={handleAddClassModalClose}
        overlayContainer={classes.overlayContainer}
      >
        <AddClassModal
          curriculumProgram={curriculumProgram}
          onClickClose={handleAddClassModalClose}
          onClickAddClasses={handleAddClasses}
          coursesSearchText={coursesSearchText}
          changeCoursesSeachText={setCoursesSearchText}
          courses={courses}
          portalType={portalType}
          selectedTimeSlot={selectedTimeSlot}
          courseIdsOfScoreCategorySet={courseIdsOfScoreCategorySet}
          scoreCategoryLabel={scoreCategoryLabel}
          subjectName={subjectName}
          isQt={isQt}
          isDpPa={isDpPa}
          isDpEp={isDpEp}
          grades={grades}
          subjects={subjects}
          subjectsField={subjectsField}
          categoryDetails={categoryDetails}
          courseId={courseId}
          filteredStaffCoursesIds={filteredStaffCoursesIds}
          learningCourseId={learningCourseId}
          selectedGradeIds={selectedGradeIds}
          onGradeFilterChange={setSelectedGradeIds}
        />
      </UIModal>
    );
  };

  const renderClassWiseSettings = () => {
    return (
      <AssignClassSettingsDataGridWrapper
        courses={courses}
        courseId={courseId}
        isDuplicateAssignment={isDuplicateAssignment}
        classWiseSettingsToDisplay={classWiseSettingsToDisplay}
        assignmentId={assignmentId}
        portalType={portalType}
        mode={mode}
        assignmentTags={assignmentTags}
        genericFolders={genericFolders}
        updateSelectedItems={updateSelectedItems}
        isSubmissionPendingForAll={isSubmissionPendingForAll}
        state={state}
        selectedTimeSlot={selectedTimeSlot}
        assignmentData={assignmentData}
        isDraftMode={isDraftMode}
        handleAddClassClick={handleAddClassClick}
        currentTimelineSettings={currentTimelineSettings}
        isStudentSubmissionEnabled={isStudentSubmissionEnabled}
        isFixedStartTime={isFixedStartTime}
        timedTestDuration={timedTestDuration}
        dueTimePreference={dueTimePreference}
        filteredStaffCoursesIds={filteredStaffCoursesIds}
        assessmentTitle={title}
        isIncludedInCourseFlow={isIncludedInCourseFlow}
        updateSelectedClassItems={updateSelectedClassItems}
        assignmentDefaultSettings={assignmentDefaultSettings}
        isLockScreenEnabled={isLockScreenEnabled}
        isSafeBrowserEnabled={isSafeBrowserEnabled}
        isTimedTestEnabled={isTimedTestEnabled}
        isWorksheet={isWorksheet}
      />
    );
  };

  const renderWorksheetSettings = () => {
    const worksheetSettings = getWorksheetSettings({
      t,
      updateSelectedItems,
      isWorksheet,
      isTimedTestEnabled,
      isLockScreenEnabled,
      isSafeBrowserEnabled,
      timedTestDuration,
    });

    return (
      <DeliverySettingsV2
        {...worksheetSettings}
        updateSelectedItems={updateSelectedItems}
        isTimedTestEnabled={isTimedTestEnabled}
        isFixedStartTime={isFixedStartTime}
        isLockScreenEnabled={isLockScreenEnabled}
        isSafeBrowserEnabled={isSafeBrowserEnabled}
        mode={mode}
        t={t}
        showWorksheetTypesHeading={false}
        sourceAssignmentWorksheetSettings={sourceAssignmentWorksheetSettings}
      />
    );
  };

  const renderCommonSettings = () => {
    const commonSettings = getAssignmentCommonSettings({
      t,
      studentTemplates,
      isTurnitinEnabledForGrade,
      contentType,
      isExcludedFromFinalGradeCalculationEnabled,
      isQt,
      isWorksheet,
      isFromClassMaterials,
      assessmentType,
      isStudentSubmissionEnabled,
      isClassDiscussionEnabled,
      isSimilarityCheckEnabled,
      isExcludedFromFinalGradeCalculation,
      isSharedWithClassMaterial,
      isOnScreenCalculatorEnabled,
      showStudentResponse,
      isIncludedInCourseFlow,
      isCourseFlow,
      learningCourseId,
      curriculumProgramId: _.get(curriculumProgram, "id"),
      isDpPa,
      isDpEp,
      courseId,
      primaryGradeId,
      taskTypeGroup,
      hasUnitPlan,
    });

    return (
      <AssignmentCommonSettings
        commonSettings={commonSettings}
        updateSelectedItems={updateSelectedItems}
        sourceAssignmentCommonSettings={sourceAssignmentCommonSettings}
        doesStaffHaveAccessToAllClasses={doesStaffHaveAccessToAllClasses}
      />
    );
  };

  const renderSettingGroupContent = key => {
    switch (key) {
      case "CLASS_WISE_SETTINGS":
        return renderClassWiseSettings();
      case "WORKSHEET_SETTINGS":
        return renderWorksheetSettings();
      default:
        return renderCommonSettings();
    }
  };

  const renderSettingGroups = () => {
    const settingGroups = getSettingGroups({ t, isWorksheet });
    return (
      <>
        {_.map(settingGroups, group => {
          const { key, icon, label } = group;
          const isClassWiseSettingsGroup = key === "CLASS_WISE_SETTINGS";

          return (
            <div className={classes.groupContainer} key={key}>
              <div className={classes.groupHeader}>
                <div className={classes.groupTitle}>
                  {icon}
                  <div className="text-label-l">{label}</div>
                </div>
                {isClassWiseSettingsGroup ? (
                  <div className={classes.buttonContainer}>
                    {!isWorksheet && !isAITutor && (
                      <Tooltip
                        dsVersion="2.0"
                        tooltip={t(
                          "classRoom:no_access_to_change_common_assignment_settings"
                        )}
                        placement={"left"}
                        overlayInnerStyle={styles.tooltipOverlayInnerStyle}
                        showArrow={true}
                        trigger={
                          !doesStaffHaveAccessToAllClasses ? ["hover"] : [null]
                        }
                      >
                        <div className={classes.submissionToggle}>
                          <ToggleSwitch
                            dsVersion="2.0"
                            onChange={handleSubmissionToggleChange}
                            checked={isStudentSubmissionEnabled}
                            testId={"assignmentPostSettings-submission"}
                            disabled={!doesStaffHaveAccessToAllClasses}
                          />
                          <div className="text-label">
                            {t("common:submission")}
                          </div>
                          {ACLStore.can(
                            "FeatureFlag:ShowAssignNotificationPreference"
                          ) &&
                          !isStudentSubmissionEnabled &&
                          (mode === "create" ||
                            _.includes(
                              ["DRAFT", "SCHEDULED"],
                              assignmentState
                            )) ? (
                            <Tooltip
                              showArrow={true}
                              tooltip={
                                isSendAssignedNotification
                                  ? t(
                                      "classRoom:students_will_be_notified_on_assign"
                                    )
                                  : t(
                                      "classRoom:students_will_not_be_notified_on_assign"
                                    )
                              }
                            >
                              <IconButton
                                dsVersion="2.0"
                                icon={
                                  isSendAssignedNotification ? (
                                    <BellRingOutlined />
                                  ) : (
                                    <BellCancelledOutlined />
                                  )
                                }
                                size="medium"
                                variant="neutral"
                                type="plain"
                                onClick={handleToggleSendAssignNotification}
                              />
                            </Tooltip>
                          ) : null}
                        </div>
                      </Tooltip>
                    )}
                    {showAddClassBtn ? (
                      <div className={classes.classesDropdown}>
                        <Button
                          dsVersion="2.0"
                          variant="neutral"
                          type="plain"
                          icon={<AddCircleOutlined />}
                          onClick={handleAddClassClick}
                          testId={"assignmentPostSettings-addClass"}
                        >
                          {_.isEmpty(courses)
                            ? t("common:add_class")
                            : t("common:add_another_class")}
                        </Button>
                      </div>
                    ) : null}

                    {isFixedStartTime ? null : (
                      <AdvancedTimelineSettings
                        currentTimelineSettings={currentTimelineSettings}
                        handleTimelineSettingsChange={
                          handleTimelineSettingsChange
                        }
                        shouldDisableClosesOnSetting={
                          !isStudentSubmissionEnabled
                        }
                        disabled={!doesStaffHaveAccessToAllClasses}
                        studentTemplates={studentTemplates}
                        testId={
                          "assignmentPostSettings-configureAssignSettings"
                        }
                      />
                    )}
                  </div>
                ) : null}
              </div>
              {renderSettingGroupContent(key)}
            </div>
          );
        })}
      </>
    );
  };

  const renderBody = () => {
    return (
      <div className={classes.bodyContainer}>
        {renderSettingGroups()}
        {showAddClassModal ? renderAddClassModal() : null}
      </div>
    );
  };

  return (
    <>
      <Modal
        dsVersion="2.0"
        width="1316px"
        height="748px"
        onClose={handleClose}
      >
        <Modal.Header title={getModalHeaderTitle()} hasSeparator={true} />
        <Modal.Body isScrollable={true}>{renderBody()}</Modal.Body>
        <Modal.Footer hasSeparator={true}>
          <div className={classes.footerContainer}>
            <div></div>
            <div className={classes.footerRightContainer}>
              <Button
                dsVersion="2.0"
                variant="neutral"
                type="outlined"
                onClick={handleDraftOrCancelClick}
                testId={`assignmentPostSettings-${
                  isDraftMode ? "saveAsDraft" : "cancel"
                }`}
              >
                {isDraftMode ? t("common:save_as_draft") : t("common:cancel")}
              </Button>
              <Button
                dsVersion="2.0"
                variant="progressive"
                onClick={handleAssignOrScheduleClick}
                testId={`assignmentPostSettings-${
                  isDraftMode ? "assign" : "saveChanges"
                }`}
                shortcut={{ code: "Enter", shiftKey: true }}
              >
                {doneButtonLabel}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {showFullScreenLoader ? <FullScreenLoader /> : null}
    </>
  );
};

const mapStateToProps = state => {
  const currentCurriculumProgramId = getSelectedCurriculumId(state);
  const academicYearId = getSelectedAcademicYearId(state);
  const selectedGrades = getSelectedGrades(state);
  const isAITutorUpdated = _.get(
    state,
    "assessment.aiData.isAITutorUpdated",
    false
  );
  const isLtiAttachmentDeleted = _.get(
    state,
    "assessment.isLtiAttachmentDeleted",
    false
  );

  return {
    academicYearId,
    isAITutorUpdated,
    currentCurriculumProgramId,
    selectedGrades,
    isLtiAttachmentDeleted,
  };
};

const getDerivedProps = ownProps => {
  const {
    mode,
    isFromUnitPlan,
    sourceFrom,
    curriculumProgram = {},
    state: assignmentState,
    portalType,
    selectedGrades,
    currentCurriculumProgramId,
    grades,
  } = ownProps;

  const userInfo = getUserInfo({ portalType });
  const userId = _.get(userInfo, "id");
  const organizationId = _.get(userInfo, "org_id");

  const courseIds = _.map(ownProps?.courses, item => item?.id);

  // Get grades from Redux state or fallback to grades prop (for courseFlow context)
  const gradesToUse = !_.isNil(selectedGrades) ? selectedGrades : grades || [];

  const turnitinEnabledGrades = getSettingValue({
    name: "TurnitinEnabledGrades",
    organizationId,
    readFromUserAndOrgLevel: true,
  });
  const isTurnitinEnabled = isTurnitinEnabledForGrade(
    gradesToUse,
    turnitinEnabledGrades
  );

  const isFromClassMaterials = sourceFrom == "CLASS_MATERIALS";
  const isDraftMode =
    (mode === "create" || assignmentState === "DRAFT") && !isFromUnitPlan;

  const isExcludedFromFinalGradeCalculationEnabled = ACLStore.can(
    "FeatureFlag:isExcludedFromFinalGradeCalculationEnabled"
  );
  const showCalendarAssignmentByScheduleDate = _.isEqual(
    getCalendarEventsViewSettingValue({
      organizationId,
      currentCurriculumProgramId,
    }),
    "SCHEDULED_DATE"
  );

  const userTimelineSettingsPreference = getSettingValue({
    name: "AssignmentTimelineSettings",
    readFromUserAndOrgLevel: true,
  });

  return {
    userId,
    courseIds,
    sourceFrom,
    isDraftMode,
    isData: true,
    isLoading: false,
    curriculumProgram,
    isTurnitinEnabledForGrade: isTurnitinEnabled,
    isExcludedFromFinalGradeCalculationEnabled,
    showCalendarAssignmentByScheduleDate,
    isFromClassMaterials,
    userTimelineSettingsPreference,
    isLearningCoursesEnabled: getIsLearningCoursesEnabled(),
    currentCurriculumProgramId,
  };
};

const mapActionCreators = {
  setToastMsg,
  updateSettings,
  setLtiAttachmentDeleted,
};

const getDefaultCourseDataFromSourceAssignment = _.memoize(
  sourceAssignmentData => {
    const {
      deadline,
      state: { state, publishedAt } = {},
      closeSubmissionDate,
      visibility,
    } = sourceAssignmentData || {};

    const {
      dateString: deadlineDate,
      timeString: deadlineTime,
    } = getDateTimeStringsBasedOnFormat({
      inputDateTimeString: deadline,
    });

    return {
      state: {
        state,
        scheduleDate: publishedAt
          ? moment(publishedAt).format("YYYY-MM-DD")
          : null,
        scheduleTime: publishedAt ? moment(publishedAt).format("HH:mm") : null,
      },
      deadlineDate,
      deadlineTime,
      closeSubmissionDate: getFormattedCloseDateTimeString(closeSubmissionDate),
      visibility,
    };
  },
  JSON.stringify
);

export default compose(
  connect(mapStateToProps, mapActionCreators),
  withDerivedProps(getDerivedProps),
  graphql(getAssessmentQuery, {
    skip: ({ contentType, leId }) => contentType != "ASSESSMENT" || !leId,
    name: "getAssessment",
    options: ({ leId, portalType, academicYearId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: leId,
        portalType,
        collaboratorsFilter: {
          userAcademicYearId: academicYearId,
        },
      },
      context: { useHttpLink: true },
    }),
    props: ({ getAssessment, ownProps: { isData, isLoading } }) => {
      const assessmentDetails = _.get(getAssessment, "node", {});

      return {
        ...getAssessmentData(assessmentDetails),
        isData: !_.isEmpty(assessmentDetails) && isData,
        isLoading:
          _.includes([1, 2], getAssessment?.networkStatus) || isLoading,
      };
    },
  }),
  graphql(getStaffCourseGenericFoldersBasicDetailsQuery, {
    name: "getStaffCourseGenericFoldersDetails",
    options: ({ userId, portalType, currentCurriculumProgramId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: userId,
        folderType: "CLASSROOM",
        portalType,
        filters: {
          fetchOnlyMappedCourses: false,
          curriculumProgramIds: currentCurriculumProgramId
            ? [currentCurriculumProgramId]
            : undefined,
          archivalState: "ACTIVE",
        },
      },
    }),
    props({
      getStaffCourseGenericFoldersDetails,
      ownProps: { isData, isLoading },
    }) {
      const data = _.get(getStaffCourseGenericFoldersDetails, "node", {});

      return {
        genericFolders: getGenericFolders(data),
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes(
            [1, 2],
            getStaffCourseGenericFoldersDetails?.networkStatus
          ) || isLoading,
      };
    },
  }),
  // This query is to get source assignment details when creating a duplicate assignment to prefill dates and visibility
  graphql(getSourceAssignmentDetailsQuery, {
    skip: ({ isDuplicateAssignment, sourceAssignmentId }) =>
      !isDuplicateAssignment || _.isEmpty(sourceAssignmentId),
    name: "getSourceAssignmentDetails",
    options: ({ sourceAssignmentId, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: sourceAssignmentId,
        portalType,
      },
    }),
    props: ({ getSourceAssignmentDetails }) => {
      const sourceAssignmentData = _.get(
        getSourceAssignmentDetails,
        "node",
        {}
      );
      const sourceAssignmentCommonSettings = _.pick(sourceAssignmentData, [
        "isStudentSubmissionEnabled",
        "isClassDiscussionEnabled",
        "isExcludedFromFinalGradeCalculation",
        "isSharedWithClassMaterial",
        "isOnScreenCalculatorEnabled",
        "isSimilarityCheckEnabled",
      ]);
      const sourceAssignmentWorksheetSettings = _.pick(sourceAssignmentData, [
        "isTimedTestEnabled",
        "isFixedStartTime",
        "isLockScreenEnabled",
        "isSafeBrowserEnabled",
        "timedTestDuration",
      ]);

      return {
        defaultCourseFromSourceAssignment: getDefaultCourseDataFromSourceAssignment(
          sourceAssignmentData
        ),
        sourceAssignmentWorksheetSettings,
        sourceAssignmentCommonSettings,
      };
    },
  }),
  withControlParameters({
    skip: ({ isLearningCoursesEnabled }) => !isLearningCoursesEnabled,
    skipContextSetup: true,
    options: ({ curriculumProgram, learningCourseId, primaryGradeId }) => {
      const learningCourseContexts = generateRnPContextsByLevel({
        level: "LEARNING_COURSE",
        learningCourseId,
        curriculumProgramId: _.get(curriculumProgram, "id"),
        gradeId: primaryGradeId,
      });
      return {
        variables: {
          filters: {
            module: "LEARNING_COURSE_FLOW",
            appType: ["TEACHER_WEB"],
            contexts: learningCourseContexts,
          },
        },
      };
    },
  }),
  withControlParameters({
    skip: ({ isLearningCoursesEnabled }) => isLearningCoursesEnabled,
    skipContextSetup: true,
    options: ({ curriculumProgram, courseId, primaryGradeId }) => {
      const courseContexts = generateRnPContextsByLevel({
        level: "COURSE",
        courseId,
        curriculumProgramId: _.get(curriculumProgram, "id"),
        gradeId: primaryGradeId,
      });
      return {
        variables: {
          filters: {
            module: "CLASS_FLOW",
            appType: ["TEACHER_WEB"],
            contexts: courseContexts,
          },
        },
      };
    },
  }),
  WithConfigurationsAndPreferences({
    skip: ({ organizationId, currentCurriculumProgramId, courseId }) =>
      !organizationId || !currentCurriculumProgramId || !courseId,
    options: ({ organizationId, currentCurriculumProgramId, courseId }) => ({
      fetchPolicy: "cache-and-network",
      variables: generateConfigurationQueryVariablesV2({
        src: "ORG_CURRICULUM_COURSE_LEVEL",
        organizationId,
        modules: ["CLASS_FLOW"],
        currentCurriculumProgramId,
        courseId,
      }),
    }),
    getConfigurationKeys: () => ["ORGANIZATION", "CURRICULUM", "COURSE"],
  }),
  WithOnlySettingStep,
  withLoader
)(AssignmentSettings);
